import React, { useState } from 'react';
import { User } from 'lucide-react';
import { imageUrlService } from '@/services/ImageUrlService';
import { useImageUrl } from '@/hooks/useImageUrl';
import { debugLogger } from '@/utils/debugLogger';
import { getAvatarGradient } from '@/utils/avatarGradient';

interface FeedProfileThumbnailProps {
  src: string;
  alt: string;
  username?: string;
  className?: string;
}

export const FeedProfileThumbnail: React.FC<FeedProfileThumbnailProps> = ({
  src,
  alt,
  username,
  className = ''
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [fallbackUrls, setFallbackUrls] = useState<string[]>([]);
  const [currentFallbackIndex, setCurrentFallbackIndex] = useState(0);
  const [showShimmer, setShowShimmer] = useState(true);

  // Get 500x500 card variant URL using async hook (supports v1.5 signed URLs)
  const { url: processedUrl, isLoading: urlLoading } = useImageUrl(src, 'card');

  React.useEffect(() => {
    if (processedUrl) {
      setCurrentUrl(processedUrl);
      
      // Generate fallbacks for backward compatibility with v1 public photos
      // (v1.5 signed URLs don't support fallbacks)
      const fallbacks = imageUrlService.generateFallbackUrls(src, 'card');
      setFallbackUrls(fallbacks);
    }
  }, [processedUrl, src]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    setShowShimmer(false);
  };

  const handleImageError = () => {
    // Try fallback URLs if available
    if (currentFallbackIndex < fallbackUrls.length) {
      const nextFallback = fallbackUrls[currentFallbackIndex];
      setCurrentUrl(nextFallback);
      setCurrentFallbackIndex(prev => prev + 1);
      return;
    }
    
    setHasError(true);
    setIsLoaded(false);
  };

  if (!currentUrl || hasError) {
    const gradientClass = getAvatarGradient(username || alt);
    return (
      <div className={`${gradientClass} flex items-center justify-center h-full w-full ${className}`}>
        <User className="w-1/2 h-1/2 text-white/80" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ height: '100%' }}>
      {/* Shimmer loading state */}
      {showShimmer && !isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-white/10 to-white/5 animate-shimmer z-30" />
      )}
      
      {/* Loading placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 z-20" />
      )}
      
      {/* Background image with blur and cover */}
      <div 
        className={`absolute inset-0 transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundImage: `url(${currentUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(5px)'
        }}
      />
      
      {/* Main image with contain and higher z-index */}
      <div 
        className={`absolute inset-0 z-10 transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundImage: `url(${currentUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Hidden img for loading detection - with cache busting */}
      <img
        src={currentUrl}
        alt={alt}
        className="sr-only"
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="eager"
      />
    </div>
  );
};

export default FeedProfileThumbnail;
